import React, { useState } from 'react';
import theme from '../../../theme/globalTheme';
import { Upload, Download, FileText, Trash2 } from 'lucide-react';

const CSVCleaner = () => {
  const [originalFile, setOriginalFile] = useState(null);
  const [cleanedData, setCleanedData] = useState('');
  const [stats, setStats] = useState({ original: 0, cleaned: 0, removed: 0 });
  const [isProcessing, setIsProcessing] = useState(false);

  // Headers to remove (case insensitive)
  const headersToRemove = [
    'MOYEN DE PAIEMENT',
    'TYPE CHARTER',
    'DATE CHARTER',
    'MONTANT TOTAL (CB/ONLINE/FACTURE)',
    'MONTANT PAYE CASH',
    'VIP SERVICES',
    'NOM CLIENT',
    'NOM PAYEUR',
    'CHARTERER'
  ];

  const isDateOnlyLine = (line) => {
    // Remove all non-alphanumeric characters except date separators to isolate the date
    const cleaned = line.replace(/[^\d\/\-\.]/g, '').trim();
    
    // If the line is empty after cleaning, it's not a date-only line
    if (!cleaned) return false;
    
    // Check for various date formats
    const datePatterns = [
      /^\d{1,2}\/\d{1,2}\/\d{4}$/,        // DD/MM/YYYY or D/M/YYYY
      /^\d{1,2}-\d{1,2}-\d{4}$/,          // DD-MM-YYYY or D-M-YYYY
      /^\d{4}-\d{1,2}-\d{1,2}$/,          // YYYY-MM-DD or YYYY-M-D
      /^\d{1,2}\.\d{1,2}\.\d{4}$/,        // DD.MM.YYYY or D.M.YYYY
      /^\d{4}\/\d{1,2}\/\d{1,2}$/,        // YYYY/MM/DD or YYYY/M/D
      /^\d{1,2}\/\d{1,2}\/\d{2}$/,        // DD/MM/YY or D/M/YY
      /^\d{1,2}-\d{1,2}-\d{2}$/,          // DD-MM-YY or D-M-YY
      /^\d{1,2}\.\d{1,2}\.\d{2}$/         // DD.MM.YY or D.M.YY
    ];
    
    // Check if the cleaned line matches any date pattern
    const isDate = datePatterns.some(pattern => pattern.test(cleaned));
    
    // Additional check: ensure the original line doesn't contain substantial text beyond the date
    // Remove the date part and see if there's meaningful content left
    if (isDate) {
      const withoutDate = line.replace(/\*?\*?\d{1,2}[\/\-\.]\d{1,2}[\/\-\.]\d{2,4}\*?\*?/g, '').trim();
      const remainingContent = withoutDate.replace(/[^\w]/g, ''); // Remove all non-word characters
      
      // If there are fewer than 3 alphanumeric characters left, consider it a date-only line
      return remainingContent.length < 3;
    }
    
    return false;
  };

  const shouldRemoveLine = (line) => {
    const trimmedLine = line.trim();
    
    // Remove empty lines
    if (!trimmedLine) return true;
    
    // Remove header lines (case insensitive)
    if (headersToRemove.some(header => 
      trimmedLine.toLowerCase().includes(header.toLowerCase())
    )) {
      return true;
    }
    
    // Remove date-only lines
    if (isDateOnlyLine(trimmedLine)) return true;
    
    return false;
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'text/csv') {
      setOriginalFile(file);
      processFile(file);
    } else {
      alert('Please upload a CSV file');
    }
  };

  const processFile = async (file) => {
    setIsProcessing(true);
    
    try {
      const text = await file.text();
      const lines = text.split('\n');
      
      const cleanedLines = lines.filter(line => !shouldRemoveLine(line));
      const cleanedContent = cleanedLines.join('\n');
      
      setCleanedData(cleanedContent);
      setStats({
        original: lines.length,
        cleaned: cleanedLines.length,
        removed: lines.length - cleanedLines.length
      });
    } catch (error) {
      console.error('Error processing file:', error);
      alert('Error processing file');
    }
    
    setIsProcessing(false);
  };

  const downloadCleanedFile = () => {
    if (!cleanedData) return;
    
    const blob = new Blob([cleanedData], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = originalFile ? 
      `cleaned_${originalFile.name}` : 
      'cleaned_file.csv';
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const reset = () => {
    setOriginalFile(null);
    setCleanedData('');
    setStats({ original: 0, cleaned: 0, removed: 0 });
    document.getElementById('fileInput').value = '';
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: theme.colors.bgPrimary,
      padding: theme.spacing.xl,
      maxWidth: '1200px',
      margin: '0 auto'
    }}>
      <div style={{ marginBottom: theme.spacing.xl }}>
        <h1 style={{
          fontSize: theme.typography.fontSize['3xl'],
          fontWeight: theme.typography.fontWeight.bold,
          color: theme.colors.textPrimary,
          marginBottom: theme.spacing.sm
        }}>
          🧹 CSV File Cleaner
        </h1>
        <p style={{ color: theme.colors.textSecondary }}>
          Upload your CSV file to remove header lines and date-only entries
        </p>
      </div>

      {/* Upload Section */}
      <div style={{ marginBottom: theme.spacing.xl }}>
        <div style={{
          background: theme.colors.bgPrimary,
          border: `2px dashed ${theme.colors.border}`,
          borderRadius: theme.borderRadius.card,
          padding: theme.spacing.xxl,
          textAlign: 'center',
          transition: theme.transitions.normal,
          cursor: 'pointer'
        }}>
          <Upload style={{
            margin: '0 auto',
            width: '48px',
            height: '48px',
            color: theme.colors.textTertiary,
            marginBottom: theme.spacing.md
          }} />
          <label htmlFor="fileInput" style={{ cursor: 'pointer' }}>
            <span style={{
              fontSize: theme.typography.fontSize.lg,
              fontWeight: theme.typography.fontWeight.medium,
              color: theme.colors.textPrimary
            }}>
              Choose CSV file
            </span>
            <input
              id="fileInput"
              type="file"
              accept=".csv"
              onChange={handleFileUpload}
              style={{ display: 'none' }}
            />
          </label>
          <p style={{
            fontSize: theme.typography.fontSize.sm,
            color: theme.colors.textSecondary,
            marginTop: theme.spacing.sm
          }}>
            Select a CSV file to clean
          </p>
        </div>
      </div>

      {/* Processing Status */}
      {isProcessing && (
        <div style={{
          marginBottom: theme.spacing.lg,
          padding: theme.spacing.lg,
          borderRadius: theme.borderRadius.card,
          background: theme.colors.bgSecondary,
          border: `1px solid ${theme.colors.border}`,
          boxShadow: theme.effects.shadowSm
        }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{
              width: '20px',
              height: '20px',
              border: `2px solid ${theme.colors.border}`,
              borderTopColor: theme.colors.primary,
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              marginRight: theme.spacing.md
            }}></div>
            <span style={{ color: theme.colors.textPrimary }}>Processing file...</span>
          </div>
        </div>
      )}

      {/* File Info and Stats */}
      {originalFile && !isProcessing && (
        <div style={{
          marginBottom: theme.spacing.lg,
          padding: theme.spacing.lg,
          borderRadius: theme.borderRadius.card,
          background: theme.colors.bgPrimary,
          border: `1px solid ${theme.colors.border}`,
          boxShadow: theme.effects.shadowSm
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: theme.spacing.lg
          }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <FileText style={{
                width: '20px',
                height: '20px',
                color: theme.colors.textSecondary,
                marginRight: theme.spacing.sm
              }} />
              <span style={{
                fontWeight: theme.typography.fontWeight.medium,
                color: theme.colors.textPrimary
              }}>
                {originalFile.name}
              </span>
              <span style={{
                fontSize: theme.typography.fontSize.sm,
                color: theme.colors.textSecondary,
                marginLeft: theme.spacing.sm
              }}>
                ({(originalFile.size / 1024).toFixed(1)} KB)
              </span>
            </div>
            <button
              onClick={reset}
              style={{
                display: 'flex',
                alignItems: 'center',
                fontSize: theme.typography.fontSize.sm,
                color: theme.colors.error,
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: theme.spacing.sm,
                borderRadius: theme.borderRadius.md,
                transition: theme.transitions.normal
              }}
            >
              <Trash2 style={{ width: '16px', height: '16px', marginRight: theme.spacing.xs }} />
              Remove
            </button>
          </div>

          {cleanedData && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr',
              gap: theme.spacing.lg,
              textAlign: 'center'
            }}>
              <div style={{
                padding: theme.spacing.md,
                borderRadius: theme.borderRadius.md,
                border: `1px solid ${theme.colors.border}`,
                background: theme.colors.bgSecondary
              }}>
                <div style={{
                  fontSize: theme.typography.fontSize['2xl'],
                  fontWeight: theme.typography.fontWeight.bold,
                  color: theme.colors.textPrimary
                }}>
                  {stats.original}
                </div>
                <div style={{
                  fontSize: theme.typography.fontSize.sm,
                  color: theme.colors.textSecondary
                }}>
                  Original Lines
                </div>
              </div>
              <div style={{
                padding: theme.spacing.md,
                borderRadius: theme.borderRadius.md,
                border: `1px solid ${theme.colors.border}`,
                background: theme.colors.bgSecondary
              }}>
                <div style={{
                  fontSize: theme.typography.fontSize['2xl'],
                  fontWeight: theme.typography.fontWeight.bold,
                  color: theme.colors.error
                }}>
                  {stats.removed}
                </div>
                <div style={{
                  fontSize: theme.typography.fontSize.sm,
                  color: theme.colors.textSecondary
                }}>
                  Removed Lines
                </div>
              </div>
              <div style={{
                padding: theme.spacing.md,
                borderRadius: theme.borderRadius.md,
                border: `1px solid ${theme.colors.border}`,
                background: theme.colors.bgSecondary
              }}>
                <div style={{
                  fontSize: theme.typography.fontSize['2xl'],
                  fontWeight: theme.typography.fontWeight.bold,
                  color: theme.colors.textPrimary
                }}>
                  {stats.cleaned}
                </div>
                <div style={{
                  fontSize: theme.typography.fontSize.sm,
                  color: theme.colors.textSecondary
                }}>
                  Clean Lines
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Download Section */}
      {cleanedData && !isProcessing && (
        <div style={{ marginBottom: theme.spacing.lg }}>
          <button
            onClick={downloadCleanedFile}
            style={{
              width: '100%',
              background: theme.colors.primary,
              color: '#ffffff',
              padding: `${theme.spacing.md} ${theme.spacing.lg}`,
              borderRadius: theme.borderRadius.md,
              fontWeight: theme.typography.fontWeight.medium,
              border: 'none',
              cursor: 'pointer',
              transition: theme.transitions.normal,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: theme.typography.fontSize.base,
              boxShadow: theme.effects.shadowMd
            }}
          >
            <Download style={{ width: '20px', height: '20px', marginRight: theme.spacing.sm }} />
            Download Cleaned CSV
          </button>
        </div>
      )}

      {/* Preview Section */}
      {cleanedData && !isProcessing && (
        <div style={{
          border: `1px solid ${theme.colors.border}`,
          borderRadius: theme.borderRadius.card,
          overflow: 'hidden',
          background: theme.colors.bgPrimary,
          boxShadow: theme.effects.shadowSm
        }}>
          <div style={{
            background: theme.colors.bgSecondary,
            padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
            borderBottom: `1px solid ${theme.colors.border}`
          }}>
            <h3 style={{
              fontWeight: theme.typography.fontWeight.medium,
              color: theme.colors.textPrimary,
              margin: 0
            }}>
              📄 Preview (First 10 lines)
            </h3>
          </div>
          <div style={{ padding: theme.spacing.lg }}>
            <pre style={{
              fontSize: theme.typography.fontSize.sm,
              color: theme.colors.textPrimary,
              overflowX: 'auto',
              whiteSpace: 'pre-wrap',
              maxHeight: '256px',
              overflowY: 'auto',
              margin: 0,
              fontFamily: theme.typography.fontFamily.mono
            }}>
              {cleanedData.split('\n').slice(0, 10).join('\n')}
              {cleanedData.split('\n').length > 10 && '\n... (more lines)'}
            </pre>
          </div>
        </div>
      )}

      {/* Info Section */}
      <div style={{
        marginTop: theme.spacing.xl,
        padding: theme.spacing.lg,
        background: theme.colors.infoLight,
        borderRadius: theme.borderRadius.card,
        border: `1px solid ${theme.colors.info}`,
        borderLeft: `4px solid ${theme.colors.info}`
      }}>
        <h3 style={{
          fontWeight: theme.typography.fontWeight.medium,
          color: theme.colors.textPrimary,
          marginBottom: theme.spacing.sm
        }}>
          ℹ️ What gets removed:
        </h3>
        <ul style={{
          fontSize: theme.typography.fontSize.sm,
          color: theme.colors.textSecondary,
          listStyle: 'none',
          padding: 0,
          margin: 0
        }}>
          <li style={{ marginBottom: theme.spacing.xs }}>• Lines containing header text like "MOYEN DE PAIEMENT", "TYPE CHARTER", etc.</li>
          <li style={{ marginBottom: theme.spacing.xs }}>• Lines that contain only dates (including **17/01/2025**, 01-12-2024, etc.)</li>
          <li>• Empty lines</li>
        </ul>
      </div>
    </div>
  );
};

export default CSVCleaner;